/**
 * Course Data Transformation Utilities
 * Transforms backend course data to frontend types
 */

import { Course, CourseDetails, Lesson } from '../services/LearnerService/LearningsService';

/**
 * Backend course structure from API
 * Supports both nested instructor object and flat instructor fields
 */
export interface BackendCourse {
  id: number;
  title: string;
  short_description: string;
  description: string;
  level: string;
  category_name: string;
  subcategory_name: string;
  thumbnail: string;
  total_lessons: number;
  course_duration: string;

  // Nested instructor object (some endpoints)
  instructor?: {
    id: string;
    name: string;
    dp: string;
  };

  // Flat instructor fields (other endpoints)
  instructor_id?: string;
  instructor_name?: string;
  instructor_dp?: string;

  avg_rating: number;
  is_enrolled: boolean;
  enrolled_date?: string;
  total_students?: number;
  completedLessons?: number;
}

/**
 * Backend course details from API
 */
export interface BackendCourseDetails {
  id: number;
  title: string;
  short_description: string;
  description: string;
  level: string;
  thumbnail: string;
  total_lessons: number;
  course_duration: string;

  // Flat instructor fields (not nested object)
  instructor_name: string;
  instructor_dp: string;
  instructor_about?: string;
  instructor_social_links?: string;
  creator_id?: string;

  avg_rating: number;

  // Lessons at root level (not in sections)
  lessons?: Array<{
    id: number;
    title: string;
    section_id: number;
    lesson_type: 'Content-Based' | 'ILTS';
    lesson_duration?: string;
    is_completed?: boolean;
    is_unlocked?: boolean;
    content?: {
      type: 'document' | 'mp4' | 'scorm' | 'url';
      document?: string;
      mp4?: string;
      scorm?: string;
      url?: string;
    };
    ilts_info?: {
      mode: 'Online' | 'Offline';
      meet_url?: string;
      venue?: string;
      start_date?: string;
      start_time?: string;
      end_date?: string;
      end_time?: string;
      message?: string;
    };
  }>;

  // Sections with empty lessons arrays
  sections?: Array<{
    id: number;
    title: string;
    lessons_count: number;
    lessons?: any[];
  }>;

  rating_stats?: {
    total: number;
    average: number;
    breakdown: Record<number, number | null>;
  };

  requirements?: Array<{ id: number; content: string }>;
  outcomes?: Array<{ id: number; content: string }>;
  faqs?: Array<{
    id: number;
    question: string;
    answer: string;
  }>;
  reviews?: Array<{
    id: number;
    rating: number;
    review: string;
    date_added: string;
    user: {
      name: string;
      avatar: string;
    };
  }>;

  progress?: {
    total_lessons: number;
    lessons_completed: number;
    progress_percentage: string;
    last_accessed?: string | null;
  };

  total_students?: number;
  is_enrolled?: boolean;
}

/**
 * Helper function to convert relative URLs to absolute URLs
 * Uses the same pattern as profile pictures for consistency
 */
const makeAbsoluteUrl = (url: string | null, addTimestamp: boolean = true): string | null => {
  if (!url) return null;

  // Already an absolute URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Get base URL from environment - use SOCKET_URL to match profile picture pattern
  const baseUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5111';

  // Ensure path starts with /
  let cleanUrl = url;
  if (!url.startsWith('/')) {
    cleanUrl = `/${url}`;
  }

  // Construct full URL
  const fullUrl = `${baseUrl}${cleanUrl}`;

  // Add cache-busting timestamp like profile pictures
  if (addTimestamp) {
    return `${fullUrl}?t=${Date.now()}`;
  }

  return fullUrl;
};

/**
 * Parse course duration string to total minutes
 * Handles formats like: "54 mins", "1 hour 18 mins", "2 hours", "90 minutes"
 */
const parseDurationToMinutes = (durationStr: string | null | undefined): number => {
  if (!durationStr) return 0;

  const lowerStr = durationStr.toLowerCase();
  let totalMinutes = 0;

  // Match hours (e.g., "1 hour", "2 hours", "1hr", "2hrs")
  const hoursMatch = lowerStr.match(/(\d+)\s*(hour|hr)/);
  if (hoursMatch) {
    totalMinutes += parseInt(hoursMatch[1], 10) * 60;
  }

  // Match minutes (e.g., "30 minutes", "45 mins", "30min")
  const minutesMatch = lowerStr.match(/(\d+)\s*(minute|min)/);
  if (minutesMatch) {
    totalMinutes += parseInt(minutesMatch[1], 10);
  }

  // If no hours or minutes found, try to extract just a number (assume minutes)
  if (totalMinutes === 0) {
    const numberMatch = lowerStr.match(/(\d+)/);
    if (numberMatch) {
      totalMinutes = parseInt(numberMatch[1], 10);
    }
  }

  return totalMinutes;
};

/**
 * Map backend course to frontend Course interface
 * Handles different display modes: 'my-courses', 'browse', 'wishlist'
 */
export const transformBackendCourse = (
  backendCourse: BackendCourse,
  displayMode: 'my-courses' | 'browse' | 'wishlist',
  userRole: 'trainee' | 'trainer' = 'trainee'
): Course => {
  // Parse duration to minutes
  const totalMinutes = parseDurationToMinutes(backendCourse.course_duration);
  // Convert to hours for storage (as decimal)
  const totalHours = totalMinutes / 60;

  // Handle both nested instructor object and flat instructor fields
  const instructorName = backendCourse.instructor?.name || backendCourse.instructor_name || 'Unknown Instructor';

  // Process thumbnail URL - if it's just a filename, prepend the uploads path
  let thumbnailUrl = '/default-course-image.jpg';
  if (backendCourse.thumbnail) {
    let thumbnailPath = backendCourse.thumbnail;

    if (!backendCourse.thumbnail.startsWith('/') && !backendCourse.thumbnail.startsWith('http')) {
      // Backend stores thumbnails in: /uploads/courses/thumbnail/
      thumbnailPath = `/uploads/courses/thumbnail/${backendCourse.thumbnail}`;
    }

    thumbnailUrl = makeAbsoluteUrl(thumbnailPath) || '/default-course-image.jpg';
  }

  const baseCourse: Course = {
    id: backendCourse.id,
    title: backendCourse.title,
    description: backendCourse.short_description || backendCourse.description,
    level: mapLevel(backendCourse.level),
    price: 'Free',
    image: thumbnailUrl,
    authorLabel: instructorName,
    authorLocation: backendCourse.category_name || '',
    displayMode,
    userRole,
    totalModules: backendCourse.total_lessons,
    totalHours,
    // Enrollment status from backend
    is_enrolled: backendCourse.is_enrolled,
    enrolled_date: backendCourse.enrolled_date
  };

  // Add progress fields for My Courses
  if (displayMode === 'my-courses' && backendCourse.is_enrolled) {
    const completedCount = backendCourse.completedLessons || 0;
    const totalCount = backendCourse.total_lessons || 0;
    const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    // Calculate filled blocks out of 10 for visual progress bar
    const progressFilled = totalCount > 0 ? Math.round((completedCount / totalCount) * 10) : 0;

    return {
      ...baseCourse,
      completedCount,
      totalCount,
      durationMinutes: totalHours * 60,
      progressFilled,
      progressText: `${progressPercentage}%`,
      isCompleted: completedCount === totalCount && totalCount > 0
    };
  }

  return baseCourse;
};

/**
 * Map backend level string to frontend level type
 */
const mapLevel = (level: string | null | undefined): 'Beginner' | 'Intermediate' | 'Advanced' => {
  if (!level) return 'Beginner'; // Handle null/undefined

  const normalized = level.toLowerCase().trim();

  if (normalized.includes('beginner')) return 'Beginner';
  if (normalized.includes('intermediate')) return 'Intermediate';
  if (normalized.includes('advanced')) return 'Advanced';

  return 'Beginner'; // Default fallback
};

/**
 * Transform sections and map lessons from root level into their sections
 * Lessons are provided at the root level with section_id to link back
 */
const transformSectionsWithLessons = (
  sections: Array<{
    id: number;
    title: string;
    lessons_count: number;
    lessons?: any[];
  }>,
  lessons: Array<{
    id: number;
    title: string;
    section_id: number;
    lesson_type: string;
    lesson_duration?: string;
    is_completed?: boolean;
    content?: any;
    ilts_info?: any;
  }>
) => {
  return sections.map(section => {
    // Find all lessons that belong to this section
    const sectionLessons = lessons.filter(
      lesson => lesson.section_id === section.id
    );

    // Calculate total duration for this section
    const totalMinutes = sectionLessons.reduce((sum, lesson) => {
      const match = lesson.lesson_duration?.match(/\d+/);
      return sum + (match ? parseInt(match[0], 10) : 0);
    }, 0);

    return {
      id: section.id.toString(),
      title: section.title,
      lectures: section.lessons_count,
      duration: formatDurationMinutes(totalMinutes),
      lessons: sectionLessons.map(transformLesson)
    };
  });
};

/**
 * Format duration in minutes to human-readable string
 */
const formatDurationMinutes = (totalMinutes: number): string => {
  if (totalMinutes < 60) return `${totalMinutes} mins`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

/**
 * Transform backend course details to frontend CourseDetails interface
 */
export const transformBackendCourseDetails = (
  backendData: BackendCourseDetails
): CourseDetails => {
  console.log('[courseMapper] Transforming backend course details:', backendData);

  const durationMatch = backendData.course_duration?.match(/\d+/);
  const totalHours = durationMatch ? parseInt(durationMatch[0], 10) : 0;

  // Handle rating_stats which might have Buffer in rating_breakdown
  const totalRatings = backendData.rating_stats?.total || 0;
  const avgRating = backendData.avg_rating || backendData.rating_stats?.average || 0;

  // Transform instructor bio - handle null/undefined
  const instructorBio = backendData.instructor_about
    ? (typeof backendData.instructor_about === 'string'
        ? backendData.instructor_about.split('\n').filter(p => p.trim())
        : [backendData.instructor_about])
    : ['Experienced instructor dedicated to student success.'];

  // Process thumbnail URL - if it's just a filename, prepend the uploads path
  let thumbnailUrl: string | null | undefined = undefined;
  if (backendData.thumbnail) {
    // If it's just a filename (no path), add the correct backend path
    let thumbnailPath = backendData.thumbnail;

    if (!backendData.thumbnail.startsWith('/') && !backendData.thumbnail.startsWith('http')) {
      // Backend stores thumbnails in: /uploads/courses/thumbnail/
      thumbnailPath = `/uploads/courses/thumbnail/${backendData.thumbnail}`;
    }

    thumbnailUrl = makeAbsoluteUrl(thumbnailPath) || undefined;
    console.log('[courseMapper] Thumbnail processing:', {
      original: backendData.thumbnail,
      withPath: thumbnailPath,
      absolute: thumbnailUrl
    });
  }

  // Process instructor image URL - if it's just a filename, prepend the uploads path
  let instructorImageUrl: string | null | undefined = undefined;
  if (backendData.instructor_dp) {
    let instructorImagePath = backendData.instructor_dp;

    if (!backendData.instructor_dp.startsWith('/') && !backendData.instructor_dp.startsWith('http')) {
      // Backend stores user profiles in: /uploads/users/profile_picture/
      instructorImagePath = `/uploads/users/profile_picture/${backendData.instructor_dp}`;
    }

    instructorImageUrl = makeAbsoluteUrl(instructorImagePath) || undefined;
    console.log('[courseMapper] Instructor image processing:', {
      original: backendData.instructor_dp,
      withPath: instructorImagePath,
      absolute: instructorImageUrl
    });
  }

  const transformed = {
    id: backendData.id,
    title: backendData.title || 'Untitled Course',
    subtitle: backendData.short_description || '',
    thumbnail: thumbnailUrl,
    rating: avgRating,
    totalRatings: totalRatings,
    totalStudents: backendData.total_students || 0,

    // Use flat instructor fields from API
    instructor: {
      name: backendData.instructor_name || 'Unknown Instructor',
      title: 'Instructor',
      avatar: backendData.instructor_dp || '',
      image: instructorImageUrl || '',
      rating: avgRating,
      totalRatings: totalRatings,
      totalStudents: backendData.total_students || 0,
      totalCourses: 1,
      bio: instructorBio
    },

    courseIncludes: [
      `${backendData.total_lessons || 0} lessons`,
      backendData.course_duration || '0 hours',
      'Lifetime access',
      'Certificate of completion'
    ],

    learningPoints: backendData.outcomes?.map((o) => o.content) || [],

    // Transform sections with lessons from root level
    courseModules: transformSectionsWithLessons(
      backendData.sections || [],
      backendData.lessons || []
    ),

    // Flat lessons array for lesson navigation
    lessons: (backendData.lessons || []).map(transformLesson),

    reviews: backendData.reviews?.map(transformReview) || [],
    faqs: backendData.faqs || [],

    contentStats: {
      sections: backendData.sections?.length || 0,
      lectures: backendData.total_lessons || 0,
      totalDuration: backendData.course_duration || '0 hours'
    },

    // Add enrollment status
    is_enrolled: backendData.is_enrolled || false
  };

  console.log('[courseMapper] Transformed course details:', transformed);
  return transformed;
};

/**
 * Transform backend section to frontend CourseModule
 */
const transformSection = (section: any) => ({
  id: section.id.toString(),
  title: section.title,
  lectures: section.lessons_count || 0,
  duration: estimateDuration(section.lessons_count),
  videos: section.lessons?.map(transformLesson) || []
});

/**
 * Transform backend lesson to frontend Lesson
 * Handles both Content-Based and ILTS lesson types
 * Backend returns: id, lessonType, contentType, contentUrl (already mapped), or ILTS fields
 */
const transformLesson = (lesson: any) => {
  // Handle both lesson_id (from API) and id (from other sources)
  const lessonId = lesson.lesson_id || lesson.id;

  // Base lesson properties
  const baseLesson = {
    id: lessonId ? lessonId.toString() : '0',
    title: lesson.title || 'Untitled Lesson',
    duration: lesson.lesson_duration || lesson.duration || '15 mins',
    isPreview: false,
    isCompleted: lesson.is_completed || lesson.isCompleted || false,
    isUnlocked: lesson.is_unlocked !== false, // Default to unlocked if not specified
    lessonType: lesson.lesson_type || lesson.lessonType
  };

  // Add content info for Content-Based lessons
  // Backend returns contentType and contentUrl already mapped by service/DTO
  if (baseLesson.lessonType === 'Content-Based') {
    // Use camelCase contentType from backend, with fallback to snake_case
    const contentType = lesson.contentType ||
                       lesson.content_type ||
                       lesson.lesson_content_type ||
                       (lesson.content && lesson.content.type) ||
                       'document';

    // Prefer contentUrl from backend (already mapped by service layer)
    // Fall back to computing it from individual fields for backward compatibility
    let contentUrl = lesson.contentUrl || null;

    if (!contentUrl) {
      // Fallback logic: try to extract from individual fields
      switch (contentType) {
        case 'document':
          contentUrl = lesson.documentUrl ||
                      lesson.lesson_content_document ||
                      (lesson.content && lesson.content.document) ||
                      null;
          break;
        case 'mp4':
          contentUrl = lesson.videoUrl ||
                      lesson.video_upload ||
                      (lesson.content && lesson.content.mp4) ||
                      null;
          break;
        case 'scorm':
          contentUrl = lesson.scormUrl ||
                      lesson.scorm_package ||
                      (lesson.content && lesson.content.scorm) ||
                      null;
          break;
        case 'url':
          contentUrl = lesson.externalUrl ||
                      lesson.content_url ||
                      (lesson.content && lesson.content.url) ||
                      null;
          break;
        default:
          // Fallback for unknown content types
          contentUrl = lesson.documentUrl ||
                      lesson.videoUrl ||
                      lesson.scormUrl ||
                      lesson.externalUrl ||
                      lesson.lesson_content_document ||
                      lesson.video_upload ||
                      lesson.scorm_package ||
                      lesson.content_url ||
                      null;
      }
    }

    const result: any = {
      ...baseLesson,
      contentType,
      contentUrl
    };

    // Add quiz/assessment fields if present
    if (contentType === 'quiz') {
      result.assessmentId = lesson.assessmentId || lesson.assessment_id || null;
      result.requireSectionCompletion = !!(lesson.requireSectionCompletion || lesson.require_section_completion);
      result.assessmentStartDate = lesson.assessmentStartDate || lesson.assessment_start_date || null;
      result.assessmentEndDate = lesson.assessmentEndDate || lesson.assessment_end_date || null;
      result.quizAccessible = lesson.quizAccessible;
      result.quizAccessDeniedReason = lesson.quizAccessDeniedReason || null;
    }

    return result;
  }

  // Add ILTS info for ILTS lessons
  // API structure: meet_url, venue, start_date, start_time, end_date, end_time
  if (baseLesson.lessonType === 'ILTS') {
    const iltsInfo = lesson.ilts_info || lesson.iltsInfo || {};

    return {
      ...baseLesson,
      iltsMode: iltsInfo.mode || (lesson.meet_url ? 'Online' : 'Offline'),
      iltsUrl: lesson.meet_url || iltsInfo.meet_url,
      iltsVenue: lesson.venue || iltsInfo.venue,
      iltsStartDate: lesson.start_date || iltsInfo.start_date,
      iltsStartTime: lesson.start_time || iltsInfo.start_time,
      iltsEndDate: lesson.end_date || iltsInfo.end_date,
      iltsEndTime: lesson.end_time || iltsInfo.end_time,
      iltsMessage: iltsInfo.message
    };
  }

  return baseLesson;
};

/**
 * Transform backend review to frontend Review
 */
const transformReview = (review: any) => ({
  id: review.id,
  name: review.user?.name || review.userName || '',
  avatar: review.user?.avatar || review.userAvatar || '',
  rating: review.rating,
  time: review.date_added ? new Date(review.date_added).toLocaleDateString() : '',
  comment: review.review || review.comment || ''
});

/**
 * Estimate duration based on lesson count
 * Assumes ~15 minutes per lesson
 */
const estimateDuration = (lessonCount: number): string => {
  if (!lessonCount) return '0 mins';

  const totalMinutes = lessonCount * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} mins`;
  if (minutes === 0) return `${hours}h`;

  return `${hours}h ${minutes}m`;
};